// AudioNew — worker records a new audio segment OR picks one from disk,
// uploads to audio-uploads/, and creates a placeholder /audios/{code+}
// doc. The transcodeAudioUpload CF picks it up, converts to mp3, and
// flips placeholder → false. The worker then returns to /audio and taps
// 「· 釋出」to push it to learners (existing notifyAudioReleased path).

import {
  Hairline,
  Han,
  HUD,
  InputLine,
  MeterButton,
  MonoData,
  MonoLabel,
  T,
  TextBtn,
} from "@goodbye/ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuthCtx } from "../context";
import { uploadAudioFile } from "../lib/uploadAudioFile";

type Mode = "record" | "pick";

interface Recording {
  blob: Blob;
  mimeType: string;
  /** Object URL for preview <audio>. */
  previewUrl: string;
  /** Filename hint for display only. */
  label: string;
}

/**
 * Pick the best MediaRecorder mimeType the current browser supports.
 * Chrome → audio/webm;codecs=opus, Safari → audio/mp4. The CF re-encodes
 * to mp3 regardless, so we just need something the recorder accepts.
 */
function pickRecorderMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

function formatDuration(s: number) {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export function AudioNew() {
  const navigate = useNavigate();
  const { email } = useAdminAuthCtx();

  const [mode, setMode] = useState<Mode>("record");
  const [title, setTitle] = useState("");
  const [recording, setRecording] = useState<Recording | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ code: string } | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Cleanup on unmount: stop the mic if user navigates away mid-record,
  // and revoke any preview blob URL.
  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try {
          recorderRef.current.stop();
        } catch {
          // ignore
        }
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
      if (recording) URL.revokeObjectURL(recording.previewUrl);
    };
    // recording is intentionally NOT in deps — we want the cleanup to read
    // whatever the latest preview URL is at unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetCapture() {
    if (recording) URL.revokeObjectURL(recording.previewUrl);
    setRecording(null);
    setElapsed(0);
    setError(null);
  }

  async function startRecording() {
    setError(null);
    resetCapture();
    const mime = pickRecorderMime();
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("此瀏覽器不支援錄音");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const used = recorder.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: used });
        const url = URL.createObjectURL(blob);
        setRecording({
          blob,
          mimeType: used,
          previewUrl: url,
          label: `現場錄音 · ${formatDuration(elapsedRef.current)}`,
        });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      recorder.start();
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      elapsedRef.current = 0;
      setIsRecording(true);
      setElapsed(0);
      tickRef.current = window.setInterval(() => {
        const s = Math.floor((Date.now() - startedAtRef.current) / 1000);
        elapsedRef.current = s;
        setElapsed(s);
      }, 250);
    } catch (err) {
      setError(err instanceof Error ? err.message : "無法存取麥克風");
    }
  }

  // Mirror elapsed in a ref so the recorder.onstop closure (set up at start
  // time) can read the final value without re-binding.
  const elapsedRef = useRef(0);

  function stopRecording() {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    setIsRecording(false);
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    resetCapture();
    const url = URL.createObjectURL(f);
    setRecording({
      blob: f,
      mimeType: f.type || "application/octet-stream",
      previewUrl: url,
      label: f.name,
    });
  }

  async function handleUpload() {
    if (!recording) return;
    if (!title.trim()) {
      setError("標題不能空白");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const { code } = await uploadAudioFile({
        blob: recording.blob,
        title,
        email: email ?? "unknown",
        mimeType: recording.mimeType,
      });
      setDone({ code });
    } catch (err) {
      setError(err instanceof Error ? err.message : "上傳失敗");
    } finally {
      setUploading(false);
    }
  }

  function switchMode(next: Mode) {
    if (isRecording) stopRecording();
    resetCapture();
    setMode(next);
  }

  // Success screen — confirms upload, points the worker to /audio for the
  // release step. No auto-navigate so they can read the transcode notice.
  if (done) {
    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: T.paper,
          paddingBottom: "calc(140px + env(safe-area-inset-bottom, 0px))",
        }}
      >
        <HUD label="工 作 端" />
        <div style={{ padding: "40px 32px", display: "flex", flexDirection: "column", gap: 14 }}>
          <Han size={22} color={T.ink900}>已上傳</Han>
          <MonoLabel size={12} color={T.ink500}>編號 · {done.code}</MonoLabel>
          <Hairline />
          <Han size={14} color={T.ink500} lh={1.9}>
            音檔正在轉檔(約 5-15 秒)。完成後回到「音檔列表」
            點「· 釋出」即可送給學員。
          </Han>
          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
            <TextBtn
              size={14}
              color={T.ink900}
              onClick={() => navigate("/audio")}
              style={{ padding: "10px 14px" }}
            >
              · 回 音 檔 列 表
            </TextBtn>
            <TextBtn
              size={13}
              color={T.ink500}
              onClick={() => {
                setDone(null);
                setTitle("");
                resetCapture();
              }}
              framed={false}
            >
              · 再 錄 一 段
            </TextBtn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
        paddingBottom: 100,
      }}
    >
      <HUD label="工 作 端" />
      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <MonoLabel size={12}>{email}</MonoLabel>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Han size={22} color={T.ink900}>新增音檔</Han>
          <TextBtn
            size={12}
            color={T.ink350}
            framed={false}
            onClick={() => navigate("/audio")}
          >
            · 返回
          </TextBtn>
        </div>
      </div>
      <Hairline style={{ margin: "8px 32px 0" }} />

      <div
        style={{
          padding: "20px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {/* Title */}
        <div>
          <MonoLabel
            size={11}
            color={T.ink500}
            style={{ display: "block", marginBottom: 4 }}
          >
            標 題 (必填)
          </MonoLabel>
          <InputLine
            value={title}
            onChange={setTitle}
            placeholder="例:車站的回音"
            dir="letter"
            ariaLabel="音檔標題"
          />
        </div>

        {/* Mode toggle */}
        <div
          style={{
            display: "flex",
            gap: 4,
            alignItems: "center",
            marginLeft: -8,
          }}
        >
          <TextBtn
            size={13}
            color={mode === "record" ? T.ink900 : T.ink350}
            onClick={() => switchMode("record")}
            framed={mode === "record"}
          >
            · 錄 音
          </TextBtn>
          <TextBtn
            size={13}
            color={mode === "pick" ? T.ink900 : T.ink350}
            onClick={() => switchMode("pick")}
            framed={mode === "pick"}
          >
            · 選 檔 案
          </TextBtn>
        </div>

        {/* Capture area */}
        {mode === "record" ? (
          <div
            style={{
              padding: "20px 0",
              display: "flex",
              flexDirection: "column",
              gap: 14,
              alignItems: "center",
            }}
          >
            <MonoData
              size={28}
              color={isRecording ? T.ink900 : T.ink500}
              style={{
                animation: isRecording
                  ? "breath 2.2s var(--ease-mist) infinite"
                  : undefined,
              }}
            >
              {formatDuration(elapsed)}
            </MonoData>
            {!isRecording && !recording && (
              <MeterButton icon="record" onClick={startRecording}>
                開 始 錄 音
              </MeterButton>
            )}
            {isRecording && (
              <MeterButton icon="stop" onClick={stopRecording}>
                停 止
              </MeterButton>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: "20px 0",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              alignItems: "center",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              // 2026-05-31: accept is an EXTENSION-ONLY list (no audio/* wildcard,
              // no broad MIME types). Two iOS Safari reasons:
              //   1. A bare accept="audio/*" greys out 語音備忘錄 (.m4a) recordings
              //      in the Files app (its UTI doesn't match the wildcard).
              //   2. Including audio/* (or capture-ish MIME) makes iOS show a
              //      「照片圖庫 / 錄影 / 選擇檔案」 sheet first — useless for audio.
              // An explicit extension list opens the Files browser directly AND
              // enables .m4a, so a single tap lands in the right picker with Voice
              // Memo exports selectable. The CF transcoder re-encodes whatever lands.
              accept=".m4a,.mp3,.wav,.aac,.caf,.aiff,.ogg,.webm,.mp4,.opus,.flac"
              onChange={onPickFile}
              style={{ display: "none" }}
            />
            <TextBtn
              size={14}
              color={T.ink900}
              onClick={() => fileInputRef.current?.click()}
            >
              · 從 手 機 選 音 檔
            </TextBtn>
          </div>
        )}

        {/* Preview + upload */}
        {recording && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              borderTop: `1px solid ${T.ink100}`,
              borderBottom: `1px solid ${T.ink100}`,
              padding: "14px 0",
            }}
          >
            <MonoLabel size={11} color={T.ink500}>
              預 覽
            </MonoLabel>
            <Han size={13} color={T.ink700}>
              {recording.label}
            </Han>
            <audio
              src={recording.previewUrl}
              controls
              style={{ width: "100%" }}
            />
            {/* 錯誤訊息放在按鈕「上方」、貼著按鈕、確保工作員看得到。
                之前放在外層 div 末尾、滾動時可能切到、且空 title 時
                error 出現位置離點擊位置太遠、容易錯過。 */}
            {error && (
              <Han size={14} color={T.ink900} lh={1.6}>
                ⚠ {error}
              </Han>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginTop: 6,
              }}
            >
              <TextBtn
                size={12}
                color={T.ink350}
                framed={false}
                onClick={resetCapture}
                disabled={uploading}
              >
                · 重 來
              </TextBtn>
              {/* 不用 disabled 把 onClick 整個擋掉、否則 title 空白時
                  點按鈕完全沒反應、工作員不知道少了什麼。改成 onClick
                  內檢查、空 title 直接 setError 顯示提示。 */}
              <TextBtn
                size={14}
                color={title.trim() && !uploading ? T.ink900 : T.ink350}
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? "· 上 傳 中…" : "· 上 傳"}
              </TextBtn>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
